import { useState, useDeferredValue } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AppLang } from '@/lib/format'
import { getT } from '@/lib/i18n'
import type { UpSettlementOut, UpPostOfficeOut } from '@/shared/types'

interface Props {
  lang: AppLang
  city: string
  warehouse: string
  onCityChange: (city: string) => void
  onWarehouseChange: (warehouse: string) => void
}

export function UkrposhtaFields({
  lang,
  city,
  warehouse,
  onCityChange,
  onWarehouseChange,
}: Props) {
  const T = getT(lang, 'ukrposhta')
  const [tab, setTab] = useState<'index' | 'city'>('index')
  const [indexQuery, setIndexQuery] = useState('')
  const [cityId, setCityId] = useState<number | null>(null)
  const [officeQuery, setOfficeQuery] = useState('')

  const currentCity = city ?? ''
  const currentWarehouse = warehouse ?? ''
  const deferredIndex = useDeferredValue(indexQuery.trim())
  const deferredCity = useDeferredValue(currentCity.trim())
  const deferredOffice = useDeferredValue(officeQuery.trim())

  // 1. By-index query
  const byIndexQuery = useQuery<UpPostOfficeOut[]>({
    queryKey: ['up-by-index', deferredIndex],
    queryFn: () => api.get<UpPostOfficeOut[]>(`/delivery/up/by-index?postcode=${encodeURIComponent(deferredIndex)}`),
    enabled: tab === 'index' && deferredIndex.length === 5,
    retry: false,
  })

  // 2. City query
  const citiesQuery = useQuery<UpSettlementOut[]>({
    queryKey: ['up-cities', deferredCity],
    queryFn: () => api.get<UpSettlementOut[]>(`/delivery/up/cities?q=${encodeURIComponent(deferredCity)}&limit=7`),
    enabled: tab === 'city' && deferredCity.length >= 2 && cityId === null,
    retry: false,
  })

  // 3. Offices query for selected city
  const officesQuery = useQuery<UpPostOfficeOut[]>({
    queryKey: ['up-offices', cityId],
    queryFn: () => api.get<UpPostOfficeOut[]>(`/delivery/up/offices?city_id=${cityId}&limit=100`),
    enabled: tab === 'city' && cityId !== null,
    retry: false,
  })

  const indexOffices = byIndexQuery.data ?? []
  const cityOptions = citiesQuery.data ?? []
  const officeOptions = (officesQuery.data ?? []).filter((o) =>
    deferredOffice ? o.index.includes(deferredOffice) || o.address_uk.toLowerCase().includes(deferredOffice.toLowerCase()) : true,
  )

  const isSelected = Boolean(currentCity && currentWarehouse)

  function handleSelectOffice(office: UpPostOfficeOut) {
    onCityChange(office.city_uk)
    onWarehouseChange(`Відділення ${office.index}, ${office.address_uk}`)
  }

  function handleReset() {
    onCityChange('')
    onWarehouseChange('')
    setCityId(null)
    setIndexQuery('')
    setOfficeQuery('')
  }

  return (
    <div className="space-y-3">
      {isSelected ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50/80 p-3.5 text-sm space-y-1.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-amber-900 uppercase tracking-wider">📦 Укрпошта</span>
            <button
              type="button"
              onClick={handleReset}
              className="text-xs font-semibold text-amber-800 hover:text-amber-950 underline"
            >
              {T.changeOffice}
            </button>
          </div>
          <p className="font-semibold text-gray-900">{currentCity}</p>
          <p className="text-xs text-gray-700">{currentWarehouse}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Switch tabs */}
          <div className="flex rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setTab('index')}
              className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-all ${
                tab === 'index' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {T.byIndexTab}
            </button>
            <button
              type="button"
              onClick={() => setTab('city')}
              className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-all ${
                tab === 'city' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {T.byCityTab}
            </button>
          </div>

          {/* Mode 1: By Index */}
          {tab === 'index' && (
            <div>
              <input
                type="text"
                maxLength={5}
                value={indexQuery}
                onChange={(e) => setIndexQuery(e.target.value.replace(/\D/g, ''))}
                placeholder={T.indexPlaceholder}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 font-mono"
              />
              <p className="mt-1 text-xs text-gray-400">
                {byIndexQuery.isLoading
                  ? T.loading
                  : deferredIndex.length === 5 && indexOffices.length === 0 && !byIndexQuery.isLoading
                    ? T.noOffices
                    : T.indexHint}
              </p>

              {deferredIndex.length === 5 && indexOffices.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
                  {indexOffices.map((office) => (
                    <button
                      key={office.id}
                      type="button"
                      onClick={() => handleSelectOffice(office)}
                      className="block w-full p-3 text-left hover:bg-amber-50/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs text-amber-900 bg-amber-100 px-2 py-0.5 rounded">
                          {office.index}
                        </span>
                        <span className="text-xs font-medium text-gray-600">{office.city_uk}</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-800 font-medium">{office.address_uk}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Mode 2: By City */}
          {tab === 'city' && (
            <div className="space-y-3">
              {cityId === null ? (
                <div>
                  <input
                    type="text"
                    value={currentCity}
                    onChange={(e) => onCityChange(e.target.value)}
                    placeholder={T.cityPlaceholder}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    {citiesQuery.isLoading
                      ? T.loading
                      : deferredCity.length >= 2 && cityOptions.length === 0 && !citiesQuery.isLoading
                        ? T.noCities
                        : T.cityHint}
                  </p>

                  {deferredCity.length >= 2 && cityOptions.length > 0 && (
                    <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
                      {cityOptions.map((c) => (
                        <button
                          key={c.city_id}
                          type="button"
                          onClick={() => {
                            onCityChange(c.name_uk)
                            setCityId(c.city_id)
                          }}
                          className="block w-full p-3 text-left hover:bg-gray-50 text-sm"
                        >
                          <span className="font-semibold text-gray-900 block">{c.name_uk}</span>
                          {c.region_uk && <span className="text-xs text-gray-400 block">{c.region_uk}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs">
                    <span className="font-semibold text-gray-700">🏙 {currentCity}</span>
                    <button
                      type="button"
                      onClick={() => setCityId(null)}
                      className="text-brand-600 hover:text-brand-700 font-medium"
                    >
                      {T.changeCity}
                    </button>
                  </div>

                  <input
                    type="text"
                    value={officeQuery}
                    onChange={(e) => setOfficeQuery(e.target.value)}
                    placeholder={T.officePlaceholder}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <p className="text-xs text-gray-400">
                    {officesQuery.isLoading
                      ? T.loading
                      : officeOptions.length === 0
                        ? T.noOffices
                        : T.officeHint}
                  </p>

                  {officeOptions.length > 0 && (
                    <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
                      {officeOptions.map((office) => (
                        <button
                          key={office.id}
                          type="button"
                          onClick={() => handleSelectOffice(office)}
                          className="block w-full p-2.5 text-left hover:bg-amber-50/50 text-xs"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-amber-900">{office.index}</span>
                            <span className="text-gray-500">{office.city_uk}</span>
                          </div>
                          <span className="text-gray-800 block mt-0.5">{office.address_uk}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
